import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '../../../../service/user.service';
import { User } from '../../../models/user.model';
import { PageLayoutComponent } from '../../../shared/page-layout/page-layout';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbService } from '../../../breadcrumb-service/breadcrumb-service';
import { StateContainerComponent, ViewState } from '../../../shared/state-container/state-container.component';
import { AccordionModule } from 'primeng/accordion';
import { Select } from 'primeng/select';
import { Button, ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';

import { Application } from '../../../models/application.model';
import { ApplicationService } from '../../../../service/application.service';

@Component({
  selector: 'app-user-detail',
  imports: [
    PageLayoutComponent,
    DatePipe,
    CommonModule,
    FormsModule,
    StateContainerComponent,
    AccordionModule,
    Select,
    ButtonModule,
    Tooltip,
    ToastModule,
    ConfirmDialog,
    TableModule
  ],
  templateUrl: './user-detail.html',
  providers: [MessageService, ConfirmationService]
})
export class UserDetail implements OnInit {

  user = signal<User | null>(null);
  loading = signal(false);
  error = signal<any>(null);
  availableApplications = signal<Application[]>([]);
  assignedApplications = signal<Application[]>([]);
  selectedApplicationId = signal<number | null>(null);
  loadingApplications = signal(false);
  loadingAssignedApps = signal(false);
  assigningApplications = signal(false);

  breadcrumb = [
    { label: 'Home', routerLink: '/' },
    { label: 'Users', routerLink: '/users' },
    { label: '' }
  ];

  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private applicationService = inject(ApplicationService);

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (!this.user()) return 'empty';
    return 'content';
  });

  readonly emptyStateMessage = computed(() => 'User not found or does not exist.');

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load user details. Please try again.';
  });

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    if (!id) {
      this.error.set('Invalid user ID');
      return;
    }

    this.loadUser(id);
    this.loadAvailableApplications();
    this.loadAssignedApplications(id);
  }

  loadUser(id: number) {
    this.loading.set(true);
    this.error.set(null);

    this.userService.getUserById(id).subscribe({
      next: (user) => {
        this.user.set(user);
        this.loading.set(false);
        this.breadcrumbService.updateLastBreadcrumbLabel(user.name);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  retryLoad() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.loadUser(id);
      this.loadAssignedApplications(id);
    }
  }

  loadAvailableApplications() {
    this.loadingApplications.set(true);
    this.applicationService.getApplications()
      .pipe(finalize(() => this.loadingApplications.set(false)))
      .subscribe({
        next: (apps) => {
          this.availableApplications.set(apps || []);
        },
        error: (err) => {
          console.error('Error loading applications:', err);
          this.availableApplications.set([]);
        }
      });
  }

  loadAssignedApplications(userId: number | string) {
    this.loadingAssignedApps.set(true);
    this.applicationService.getUserApplications(String(userId))
      .pipe(finalize(() => this.loadingAssignedApps.set(false)))
      .subscribe({
        next: (apps) => {
          this.assignedApplications.set(apps || []);
        },
        error: (err) => {
          console.error('Error loading assigned applications:', err);
          this.assignedApplications.set([]);
        }
      });
  }

  onApplicationSelectChange(id: number | null) {
    this.selectedApplicationId.set(id);
  }

  addApplications() {
    const user = this.user();
    const selectedId = this.selectedApplicationId();

    if (!user) return;

    if (selectedId === null || selectedId === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select an application'
      });
      return;
    }

    const existingIds = this.assignedApplications().map(a => a.id);

    if (existingIds.includes(selectedId)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Selected application is already assigned to this user'
      });
      return;
    }

    const mergedIds = [...existingIds, selectedId];

    this.assigningApplications.set(true);

    this.applicationService.assignApplicationsToUser(String(user.id), mergedIds)
      .pipe(finalize(() => this.assigningApplications.set(false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Application assigned successfully'
          });
          this.selectedApplicationId.set(null);
          this.loadAssignedApplications(user.id);
        },
        error: (err) => {
          console.error('Error assigning application:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error?.message || 'Failed to assign application'
          });
        }
      });
  }

  removeApplication(app: Application) {
    const user = this.user();
    if (!user) return;

    this.confirmationService.confirm({
      header: 'Confirm Remove',
      message: `Are you sure you want to remove "${app.name}" from this user?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.applicationService.removeApplicationFromUser(String(user.id), app.id)
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `"${app.name}" removed successfully`
              });
              this.loadAssignedApplications(user.id);
            },
            error: (err) => {
              console.error('Error removing application:', err);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err.error?.message || 'Failed to remove application'
              });
            }
          });
      }
    });
  }

  get availableOptions(): Application[] {
    const assignedIds = this.assignedApplications().map(a => a.id);
    return this.availableApplications().filter(app => !assignedIds.includes(app.id));
  }
}